/**
 * 事業者登録番号の「国の概念」カタログ（国別税モデル Step 3）。
 *
 * - JP ＝適格請求書発行事業者番号（T+13桁の検証・未登録警告・経過措置の前提）。
 *   JP 向けの文言・検証は各画面が既存のまま持ち、このカタログは出し分けの旗だけ持つ。
 * - GB / DE / FR / AU / CA ＝その国の登録番号の**呼び名だけ**を出す。書式は検証しない
 *   （国ごとに書式が違い、誤った形式チェックは正しい番号を弾く＝backend の
 *   `services/registration_numbers.py` と同じ判断）。
 * - US・カタログ外の国＝概念を出さない（仕入書類に載せる登録番号をこちらが断言できない
 *   国でラベルを推測しない＝税率プリセットの「行なし」と同じ縮退）。
 *
 * ラベルはその国の税制度の固有名詞なので UI ロケールで訳し分けない（ja の画面でも
 * "VAT number" のまま）。⚠️ 2026-08-04＝税率プリセットは53カ国へ拡張されたが、
 * 登録番号ラベルは固有名詞を確信できる7カ国のみ＝集合はもう一致しない（他国は
 * 概念を出さない縮退のまま＝誤ったラベルを推測しない）。
 */
export interface RegistrationConcept {
  /** 画面・PDF・CSV に出すラベル（国の固有名詞）。JP は各画面の既存文言を使う。 */
  label: string;
  /** JP の適格請求書制度か（T+13 検証・未登録バッジ・経過措置の対象）。 */
  isJapanInvoiceSystem: boolean;
}

const JP_CONCEPT: RegistrationConcept = {
  label: "登録番号",
  isJapanInvoiceSystem: true,
};

const CONCEPTS: Record<string, RegistrationConcept> = {
  JP: JP_CONCEPT,
  GB: { label: "VAT number", isJapanInvoiceSystem: false },
  DE: { label: "USt-IdNr.", isJapanInvoiceSystem: false },
  FR: { label: "N° TVA", isJapanInvoiceSystem: false },
  AU: { label: "ABN", isJapanInvoiceSystem: false },
  CA: { label: "GST/HST number", isJapanInvoiceSystem: false },
  // US: 再販免除（resale certificate）＝発注書に載せる登録番号の概念を出さない。
};

/**
 * 店の国 → 登録番号の概念。無い国は null（登録番号の UI・帳票行を丸ごと出さない）。
 * `country_code` の欠落（未同期・旧API）は JP 扱い＝既存の全店が日本なので安全側
 * （PurchaseOrders の `isJapanStore` と同じ規則）。
 */
export function registrationConcept(
  countryCode: string | null | undefined,
): RegistrationConcept | null {
  return CONCEPTS[(countryCode ?? "JP").trim().toUpperCase()] ?? null;
}

/** PDF/CSV ビルダーの後方互換の既定（options 未指定＝従来どおり JP の帳票）。 */
export const JP_REGISTRATION_CONCEPT = JP_CONCEPT;
